import { useState } from "react";
import { ExternalLink, Copy, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusBadge, ConfirmDialog } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { api, errMsg } from "@/lib/api";
import { portalUrl } from "@/lib/format";
import { t } from "@/i18n";

export function PublicLinkCard({ onActivated }) {
  const { garage, setGarage, canManage, access } = useGarage();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = garage.public_link_active;
  const url = portalUrl(garage.slug);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/garage/public-link", { active: !active });
      setGarage(data);
      toast.success(!active ? t("publicLink.activated") : t("publicLink.deactivated"));
      setConfirm(false);
      if (!active) onActivated?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="public-link-card">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <Globe className="h-5 w-5 text-teal-700" />
        <code className="min-w-0 flex-1 truncate text-sm text-slate-800" data-testid="public-link-url">{url}</code>
        <StatusBadge status={active && !access.blocked ? "live" : "offline"} testId="public-link-status" />
      </div>
      {access.blocked && <p className="text-sm text-red-700" data-testid="public-link-blocked-note">{t("publicLink.blocked")}</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" asChild data-testid="public-link-preview-button">
          <a href={`/g/${garage.slug}${active ? "" : "?preview=1"}`} target="_blank" rel="noreferrer">{active ? t("publicLink.open") : t("publicLink.preview")} <ExternalLink className="ml-2 h-4 w-4" /></a>
        </Button>
        <Button variant="outline" onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success(t("publicLink.copied")))} data-testid="public-link-copy-button">
          <Copy className="mr-2 h-4 w-4" /> {t("publicLink.copy")}
        </Button>
        {canManage && (
          <Button variant={active ? "outline" : "default"} className={active ? "text-red-600" : "bg-teal-600 hover:bg-teal-700"} onClick={() => setConfirm(true)} data-testid="public-link-toggle-button">
            {active ? t("publicLink.deactivate") : t("publicLink.activate")}
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        busy={busy}
        destructive={active}
        title={active ? t("publicLink.deactivateTitle") : t("publicLink.activateTitle")}
        description={active ? t("publicLink.deactivateText") : t("publicLink.activateText", { url })}
        confirmLabel={active ? t("publicLink.deactivate") : t("publicLink.activate")}
        onConfirm={toggle}
        testId="public-link-confirm-dialog"
      />
    </div>
  );
}
